import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import Footer from '../../common/layout/Footer';
import { PRIVACY_POLICY_CONFIG } from '../../constants/privacyPolicy';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage: React.FC = () => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  React.useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollableDistance = documentHeight - windowHeight;
      
      // Показуємо кнопку тільки коли користувач доскролив вниз (в кінці сторінки)
      const nearEnd = position >= scrollableDistance - 100;
      setShowScrollButton(nearEnd && scrollableDistance > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollClick = () => {
    // Завжди скролимо вверх до початку
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTocClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Функція для рендерингу тексту з активними посиланнями на email, phone та website
  const renderTextWithLinks = (text: string): (string | React.ReactElement)[] => {
    const emailRegex = /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/gi;
    const phoneRegex = /\+\d{1,3}\s?[\(\d{1,4}\)]*\s?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{1,9}/g;
    const websiteRegex = new RegExp(PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');

    let parts: (string | React.ReactElement)[] = [text];

    // Замінюємо email на посилання
    parts = parts.flatMap((part) => {
      if (typeof part !== 'string') return [part];
      const emailMatches = part.split(emailRegex);
      const emails = part.match(emailRegex) || [];
      const result: (string | React.ReactElement)[] = [];
      let emailIndex = 0;

      emailMatches.forEach((match, i) => {
        if (match) result.push(match);
        if (emailIndex < emails.length) {
          result.push(
            <a key={`email-${i}`} href={`mailto:${emails[emailIndex]}`} style={{ color: '#667eea', textDecoration: 'underline' }}>
              {emails[emailIndex]}
            </a>
          );
          emailIndex++;
        }
      });
      return result;
    });

    // Замінюємо phone на посилання
    parts = parts.flatMap((part) => {
      if (typeof part !== 'string') return [part];
      const phoneMatches = part.split(phoneRegex);
      const phones = part.match(phoneRegex) || [];
      const result: (string | React.ReactElement)[] = [];
      let phoneIndex = 0;

      phoneMatches.forEach((match, i) => {
        if (match) result.push(match);
        if (phoneIndex < phones.length) {
          result.push(
            <a key={`phone-${i}`} href={`tel:${phones[phoneIndex]}`} style={{ color: '#667eea', textDecoration: 'underline' }}>
              {phones[phoneIndex]}
            </a>
          );
          phoneIndex++;
        }
      });
      return result;
    });

    // Замінюємо website name на посилання
    parts = parts.flatMap((part) => {
      if (typeof part !== 'string') return [part];
      const websiteMatches = part.split(websiteRegex);
      const websites = part.match(websiteRegex) || [];
      const result: (string | React.ReactElement)[] = [];
      let websiteIndex = 0;

      websiteMatches.forEach((match, i) => {
        if (match) result.push(match);
        if (websiteIndex < websites.length) {
          result.push(
            <a key={`website-${i}`} href={PRIVACY_POLICY_CONFIG.WEBSITE_URL} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {websites[websiteIndex]}
            </a>
          );
          websiteIndex++;
        }
      });
      return result;
    });

    return parts;
  };

  const content = {
    ua: {
      title: 'Політика конфіденційності',
      
      intro: {
        heading: `Політика конфіденційності SaaS платформи ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}`,
        text: `Політика конфіденційності SaaS платформи ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} встановлює принципи обробки персональних даних Користувачів та поширюється на всіх осіб, які взаємодіють з ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} в якості користувачів (споживачів).`,
      },

      sections: [
        {
          id: 'data-types',
          heading: 'Види персональних даних, які збираються',
          content: [
            {
              subheading: 'Особиста контактна інформація',
              text: 'Включає будь-яку інформацію, яку ви надаєте нам, що дозволить Нам зв\'язатися з Вами, таку як Ваше ім\'я, поштова адреса, адреса електронної пошти, дані соціальної мережі або номер телефону.\n\nПравова підстава: Надання згоди або виконання договору (для реєстрації та надання послуг).',
            },
            {
              subheading: 'Дані для входу в обліковий запис',
              text: 'Будь-яка інформація, необхідна для доступу до вашого облікового запису. Наприклад, адреса електронної пошти, ім\'я користувача, Ваш пароль та/або контрольне запитання та відповідь.\n\nПравова підстава: Виконання договору (необхідно для доступу до облікового запису).',
            },
            {
              subheading: 'Інформація з комп\'ютера / мобільного пристрою',
              text: 'Будь-яка інформація про комп\'ютерну систему або інший технологічний пристрій, який Ви використовуєте для доступу до saas платформи або додатків, наприклад, IP адреса, яка використовується для підключення комп\'ютера або пристрою до Інтернету, тип операційної системи, тип та версія веб-браузера. Якщо ви отримуєте доступ до saas платформи через мобільний пристрій, наприклад, смартфон, зібрана інформація також включатиме, у випадках, коли це дозволено, унікальний ідентифікатор вашого пристрою, ідентифікатор реклами, географічне розташування та інші подібні дані мобільних пристроїв.\n\nПравова підстава: Законний інтерес (забезпечення безпеки, покращення послуг, аналітика).',
            },
            {
              subheading: 'Інформація про використання saas платформи',
              text: 'Під час навігації та взаємодії з saas платформою або інформаційними листами ми використовуємо технології автоматичного збору даних, щоб збирати певну інформацію про Вашу активність. Сюди входить інформація про те, за якими посиланнями ви переходите, які сторінки або контент переглядаєте і як довго, а також інша аналогічна інформація та статистика ваших дій, наприклад, час відгуку, помилки завантаження та тривалість відвідування певних сторінок. Ця інформація фіксується за допомогою автоматизованих технологій, таких як файли Cookie, а також збирається за допомогою третіх сторін для відстеження в аналітичних та рекламних цілях. Ви маєте право заперечити проти використання таких технологій у порядку, передбаченому даною Політикою конфіденційності та чинним законодавством.\n\nПравова підстава: Надання згоди (для файлів cookie та аналітики).',
            },
            {
              subheading: 'Контент споживачів',
              text: 'Будь-який контент, який ви створюєте, а потім ділитесь ним з нами, завантажуючи його на saas платформу, включаючи використання додатків соціальних мереж. Наприклад, фотографії, відео, персональні історії або інший подібний контент. У випадках, коли це дозволено, ми збираємо і публікуємо контент споживача у зв\'язку з різними заходами, включаючи конкурси та інші рекламні акції.\n\nПравова підстава: Надання згоди.',
            },
            {
              subheading: 'Інформація із соціальних мереж',
              text: 'Інформація, якою ви ділитеся публічно в соціальній мережі, або інформація, яка є частиною вашого профілю в соціальній мережі, а саме: основна інформація Вашого облікового запису (ім\'я, адреса електронної пошти, фото профілю, ідентифікатор користувача, список друзів та інші дані за винятком інформації про стать, дату народження й місто проживання) і будь-яка інша додаткова інформація, якою ви дозволяєте ділитися соціальній мережі. Ми отримуємо інформацію з Вашого профілю (або частину інформації) в соціальній мережі кожного разу, коли ви взаємодієте з saas платформою. Щоб дізнатися більше про те, як saas платформа отримує вашу інформацію із соціальної мережі, або відмовитися від надання такої інформації в соціальній мережі, відвідайте веб-сайт відповідної соціальної мережі.\n\nПравова підстава: Надання згоди (користувач дозволяє поділитися інформацією).',
            },
            {
              subheading: 'Інші персональні дані',
              text: 'Інші персональні дані що збираються можуть бути описані в інших розділах даної Політики конфіденційності або окремим пояснювальним текстом, контекстуально зі збором даних.\n\nОсобисті дані можуть бути вільно надані Користувачем або збиратися автоматично при використанні saas платформи.\n\nНездатність або небажання надати певні особисті дані можуть унеможливити надання послуг saas платформою.\n\nПравова підстава: Залежно від конкретних даних - надання згоди, виконання договору або законний інтерес.',
            },
          ],
        },
        {
          id: 'data-processing',
          heading: 'Спосіб обробки персональних даних',
          content: [
            {
              text: 'Контролер Даних обробляє дані користувачів в належному порядку і повинен вживати належних заходів безпеки для запобігання несанкціонованого доступу, розкриття, модифікації або несанкціонованого знищення даних.\n\nОбробка даних здійснюється за допомогою комп\'ютерів і / або ІТ-інструментів, слідуючи організаційним процедурам і режимам, суворо пов\'язаних із зазначеними цілями. Крім Контролера Даних, в деяких випадках дані можуть бути доступні для певних осіб, відповідальних і пов\'язаних з роботою SaaS платформи (керівництво, продаж, маркетинг, юридичні послуги, системне адміністрування) або зовнішніх сторін (обробників даних) призначеним в разі необхідності Власником.',
            },
            {
              subheading: 'Основні обробники даних',
              text: 'Для забезпечення функціонування платформи ми використовуємо наступних основних обробників даних:',
              list: [
                'Google LLC (OAuth автентифікація) - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Політика конфіденційності Google</a>',
                'OpenStreetMap Foundation (геолокаційні послуги) - <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">Політика конфіденційності OSM</a>',
                'LiqPay (платіжні послуги) - <a href="https://www.liqpay.ua/documentation/uk/privacy_policy" target="_blank" rel="noopener noreferrer">Політика конфіденційності LiqPay</a>',
                'Постачальники хостингу ( Render - США, Німеччина) - забезпечують зберігання та обробку даних',
              ],
            },
            {
              text: 'Всі обробники даних ретельно відібрані та зобов\'язані забезпечувати відповідний рівень захисту персональних даних відповідно до чинного законодавства.',
            },
          ],
        },
        {
          id: 'data-location',
          heading: 'Місце обробки персональних даних',
          content: [
            {
              text: 'Дані обробляються в операційних офісах контролера даних та в інших місцях, де розташовані сторони, які беруть участь в обробці. Зокрема, персональні дані можуть передаватися та оброблятися в країнах Європейської економічної зони (ЄЕЗ), Україні та за їх межами.',
            },
            {
              subheading: 'Міжнародна передача даних',
              text: 'У зв\'язку з використанням послуг третіх сторін (зокрема Google, Telegram та постачальників хостингу), персональні дані можуть передаватися за межі Європейської економічної зони та України. Зокрема, наші постачальники хостингу (Vercel, Render) розташовані в США (Сан-Франциско, Каліфорнія). Такі передачі здійснюються з дотриманням вимог законодавства про захист персональних даних.',
              list: [
                'Для передач до США та інших країн, які не забезпечують адекватний рівень захисту згідно з рішенням Європейської Комісії, ми використовуємо стандартні договірні положення (Standard Contractual Clauses) або інші відповідні механізми захисту',
                'Ми ретельно відбираємо постачальників послуг, які забезпечують відповідний рівень захисту даних',
                'Усі міжнародні передачі даних здійснюються тільки в необхідних межах для надання послуг користувачам',
              ],
            },
            {
              text: 'Користувач має право отримати додаткову інформацію про механізми захисту даних при міжнародних передачах, звернувшись до нас за контактними даними, вказаними в цій Політиці.',
            },
          ],
        },
        {
          id: 'data-retention',
          heading: 'Час зберігання персональних даних',
          content: [
            {
              text: 'Ми застосовуємо наступні конкретні терміни зберігання персональних даних залежно від їх типу та цілей обробки:',
            },
            {
              list: [
                'Дані облікового запису користувача зберігаються до моменту видалення облікового запису користувачем або протягом 3 років після останньої активності користувача',
                'Логи активності та технічні дані (дані про використання) зберігаються протягом 90 днів',
                'Дані, пов\'язані з історією покупок та транзакцій\n\nМи зберігаємо інформацію про факт здійснення вами платежу за послуги надані SaaS платформою. До такої інформації належать:\n\nДані, необхідні для ідентифікації транзакції та надання вам доступу (номер транзакції в платіжній системі, дата, час, сума, тип придбаної підписки);\n\nВаші контактні дані, пов\'язані з цією транзакцією (адреса електронної пошти, ім\'я облікового запису).\n\nЦі дані зберігаються протягом 5 років відповідно до вимог податкового законодавства України (ст. 44 Податкового Кодексу України) для цілей фінансового обліку та виконання наших юридичних зобов\'язань.\n\nЗверніть увагу: Безпосередня обробка платіжних реквізитів (даних вашої банківської картки) здійснюється нашою платіжною системою-партнером LiqPay відповідно до її Політики конфіденційності та міжнародних стандартів безпеки PCI DSS. <strong>Ми не зберігаємо та не маємо доступу до повних даних вашої банківської карти.</strong>',
                'Дані для маркетингових цілей зберігаються протягом шести місяців з моменту отримання згоди або до відкликання згоди користувачем',
                'Фотографії та контент користувачів зберігаються до видалення користувачем або протягом 30 днів після останньої активності',
                'Дані чату та підтримки зберігаються протягом 60 днів після завершення діалогу',
              ],
            },
            {
              text: 'Після закінчення зазначених термінів дані автоматично видаляються або анонімізовуються. Користувач завжди може зажадати дострокового видалення своїх даних, звернувшись до нас за контактними даними, вказаними в цій Політиці.',
            },
          ],
        },
        {
          id: 'data-purposes',
          heading: 'Мета використання зібраних персональних даних',
          content: [
            {
              text: 'Дані, що стосуються Користувача, збираються, щоб дозволити Власнику надавати свої послуги, а також для наступних цілей:',
            },
            {
              list: [
                'Зв\'язок з Користувачем (Правова підстава: Законний інтерес або виконання договору)',
                'Взаємодія з зовнішніми соціальними мережами і платформами (Правова підстава: Надання згоди)',
                'Аналітика (Правова підстава: Надання згоди або законний інтерес)',
                'Реєстрація та аутентифікація (Правова підстава: Виконання договору)',
                'Моніторинг інфраструктури (Правова підстава: Законний інтерес)',
                'Оптимізація та розподіл трафіку (Правова підстава: Законний інтерес)',
                'Взаємодія з живими чат платформами (Правова підстава: Законний інтерес)',
                'Ремаркетинг і поведінковий таргетинг (Правова підстава: Надання згоди)',
                'Управління контактами і відправкою повідомлень (Правова підстава: Надання згоди або законний інтерес)',
                'Відображення контенту з зовнішніх платформ (Правова підстава: Законний інтерес)',
                'Теплові карти (Правова підстава: Надання згоди)',
                'Взаємодія з підтримкою і зворотним зв\'язком платформ (Правова підстава: Законний інтерес)',
                'Управління базами даних користувачів (Правова підстава: Виконання договору)',
                'Управління підтримкою і контактними запитами (Правова підстава: Законний інтерес)',
              ],
            },
          ],
        },
        {
          id: 'user-rights',
          heading: 'Права Користувача',
          content: [
            {
              text: 'Згідно з чинним законодавством, Користувач має наступні права щодо своїх персональних даних:',
              list: [
                'Право на доступ до своїх персональних даних - можливість отримати інформацію про обробку Ваших даних',
                'Право на виправлення персональних даних - можливість виправити неточні або неповні дані',
                'Право на видалення персональних даних ("право на забуття") - можливість видалити свої дані за певних умов',
                'Право на обмеження обробки персональних даних - можливість обмежити обробку даних у певних випадках',
                'Право на переносимість даних - можливість отримати свої дані в структурованому форматі та передати їх іншому контролеру',
                'Право на заперечення проти обробки персональних даних - можливість заперечити проти обробки даних на основі законних інтересів',
                'Право не підлягати автоматизованому рішенню, включаючи профілювання - можливість не підлягати рішенням, які грунтуються виключно на автоматизованій обробці',
              ],
            },
            {
              text: `Для реалізації цих прав Користувач може звернутися до нас за допомогою Форми зворотного зв'язку, яка доступна на SaaS платформі за посиланням ${PRIVACY_POLICY_CONFIG.CONTACTS_URL}, або використовуючи e-mail: ${PRIVACY_POLICY_CONFIG.EMAIL}. Ми розглянемо Ваш запит протягом 30 днів та надамо відповідь.`,
            },
            {
              text: 'У випадках, передбачених чинним законодавством, таких як виконання юридичної зобов\'язання, захист законних інтересів у суді, забезпечення громадського інтересу або для цілей архівування в суспільному інтересі, SaaS платформа може продовжувати зберігати персональні дані Користувача навіть після отримання запиту на видалення.',
            },
            {
              text: 'Слід звернути увагу на те, що ми вимагаємо від наших зареєстрованих користувачів підтвердження своєї особи (наприклад, логін/адреса електронної пошти, пароль), перш ніж вони можуть отримати доступ або внести зміни у свій обліковий запис.',
            },
          ],
        },
        {
          id: 'automated-decisions',
          heading: 'Автоматизоване прийняття рішень',
          content: [
            {
              text: 'Ми не використовуємо виключно автоматизовані процеси прийняття рішень, включаючи профілювання, які мають юридичні наслідки для користувачів або суттєво впливають на них. Всі рішення щодо надання послуг, обробки даних або взаємодії з користувачами приймаються з участю людини.',
            },
            {
              text: 'Ми можемо використовувати автоматизовані інструменти для:',
              list: [
                'Аналізу використання платформи для покращення послуг',
                'Виявлення підозрілої активності для забезпечення безпеки',
                'Персоналізації контенту на основі наданих переваг користувача',
              ],
            },
            {
              text: 'У всіх випадках користувач має право на людський розгляд будь-яких рішень, що стосуються його даних, та може заперечити проти автоматизованої обробки.',
            },
          ],
        },
        {
          id: 'children-data-protection',
          heading: 'Захист даних дітей',
          content: [
            {
              text: `Наші послуги не призначені для осіб, які не досягли 18 років ("повноліття" відповідно до законодавства України). Ми не збираємо свідомо персональні дані дітей.`,
            },
            {
              text: 'Якщо ви є батьком або опікуном і виявите, що ваша дитина надала нам свої дані без вашої згоди, будь ласка, зв\'яжіться з нами. Ми вживаємо заходів для видалення такої інформації з наших серверів.',
            },
            {
              text: 'Батьки або законні представники можуть звернутися до нас для:',
              list: [
                'Перегляду персональних даних їхньої дитини',
                'Виправлення або видалення таких даних',
                'Відкликання згоди на обробку даних дитини',
              ],
            },
            {
              text: 'Ми рекомендуємо батькам активно супроводжувати онлайн-активність своїх дітей та використовувати доступні інструменти батьківського контролю.',
            },
          ],
        },
        {
          id: 'user-responsibility',
          heading: 'Відповідальність Користувача за дані третіх осіб',
          content: [
            {
              text: 'Користувачі несуть відповідальність за будь-які персональні дані третіх осіб, отримані, опубліковані або розміщені за допомогою цієї SaaS платформи. Користувачі підтверджують, що у них є згода третіх осіб на надання таких даних Власнику SaaS платформи. Забороняється завантажувати або передавати персональні дані третіх осіб без їхньої попередньої згоди.',
            },
          ],
        },
        {
          id: 'policy-changes',
          heading: 'Зміна даної політики конфіденційності',
          content: [
            {
              text: `Користувач, використовуючи дану SaaS платформу, прийняв умови цієї політики конфіденційності SaaS платформи ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} і враховує, що дана політика конфіденційності може час від часу змінюватися. Зміни, що вносяться до політики конфіденційності, публікуються SaaS платформою ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} на цій сторінці.`,
            },
            {
              text: 'Для суттєвих змін, які можуть впливати на обробку персональних даних (наприклад, зміна цілей обробки або способів використання даних), ми додатково повідомляємо користувачів активними методами, включаючи надсилання повідомлень електронною поштою або сповіщень через платформу.',
            },
            {
              text: 'Продовження використання SaaS платформи після набуття чинності змін означає вашу згоду з оновленою політикою конфіденційності.',
            },
          ],
        },
        {
          id: 'data-controller',
          heading: 'Володілець персональних даних',
          content: [
            // {
            //   text: `Контролером персональних даних є ${PRIVACY_POLICY_CONFIG.CONTROLLER_NAME}, ${PRIVACY_POLICY_CONFIG.CONTROLLER_TYPE}, розташована за адресою: ${PRIVACY_POLICY_CONFIG.CONTROLLER_LOCATION}.`,
            // },
            {
              text: `${PRIVACY_POLICY_CONFIG.CONTROLLER_REGISTRATION}`,
            },
            {
              text: `${PRIVACY_POLICY_CONFIG.CONTROLLER_REPRESENTATIVE}`,
            },
            {
              text: `З питань щодо політики конфіденційності, Користувач може зв'язатися з нами через кабінет SaaS платформи ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}, або використовуючи e-mail: ${PRIVACY_POLICY_CONFIG.EMAIL} чи телефон: ${PRIVACY_POLICY_CONFIG.PHONE}.`,
            },
            {
              text: `Контактна інформація також доступна у футері або просто внизу головної сторінки ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}`,
            },
          ],
        },
      ],
    },
    en: {
      title: 'Privacy Policy',
      
      intro: {
        heading: `${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} Privacy Policy`,
        text: `This privacy policy establishes the principles of processing personal data of Users and applies to all persons who interact with ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} as users (consumers).`,
      },

      sections: [
        {
          id: 'data-types',
          heading: 'Types of Personal Data Collected',
          content: [
            {
              subheading: 'Personal Contact Information',
              text: 'Includes any information you provide to us that allows us to contact you, such as your name, postal address, email address, social network data, or phone number.\n\nLegal basis: Consent or Performance of contract (for registration and service provision).',
            },
            {
              subheading: 'Account Login Data',
              text: 'Any information necessary to access your account. For example, email address, username, your password, and/or security questions and answers.\n\nLegal basis: Performance of contract (necessary for account access).',
            },
            {
              subheading: 'Computer/Mobile Device Information',
              text: 'Any information about the computer system or other technological device you use to access our website or applications, such as the IP address used to connect your computer or device to the Internet, operating system type, and web browser type and version. If you access the website through a mobile device, such as a smartphone, the collected information will also include, where permitted, your device\'s unique identifier, advertising ID, geographic location, and other similar mobile device data.\n\nLegal basis: Legitimate interest (security, service improvement, analytics).',
            },
            {
              subheading: 'Website Usage Information',
              text: 'While navigating and interacting with our website or newsletters, we use automated data collection technologies to collect certain information about your activity. This includes information about which links you follow, which pages or content you view and for how long, and other similar information and statistics about your actions, such as response time, loading errors, and duration of visits to specific pages. This information is recorded using automated technologies such as cookies and web beacons, and is collected by third parties for tracking in analytical and advertising purposes. You have the right to object to the use of such technologies in the manner provided by this Privacy Policy and applicable legislation.\n\nLegal basis: Consent (for cookies and analytics).',
            },
            {
              subheading: 'Consumer Content',
              text: 'Any content you create and then share with us by uploading it to our website, including the use of social media applications. For example, photos, videos, personal stories, or other similar content. Where permitted, we collect and publish consumer content in connection with various activities, including contests and other promotional offers.\n\nLegal basis: Consent.',
            },
            {
              subheading: 'Social Media Information',
              text: 'Information you share publicly on a social network or information that is part of your social network profile, namely: basic information of your account (name, email address, profile photo, user ID, friends list and other data except information about gender, date of birth and city of residence) and any other additional information you allow to be shared on the social network. We receive information from your profile (or part of the information) on a social network each time you interact with our site. To learn more about how our site obtains your information from a social network or to opt out of providing such information on a social network, visit the respective social network\'s website.\n\nLegal basis: Consent (user allows information sharing).',
            },
            {
              subheading: 'Other Personal Data',
              text: 'Other personal data that is collected may be described in other sections of this Privacy Policy or in separate explanatory text contextually with data collection.\n\nPersonal data may be freely provided by the User or collected automatically when using the site.\n\nInability or unwillingness to provide certain personal data may make it impossible to provide services through our site.\n\nLegal basis: Depending on specific data - consent, contract performance, or legitimate interest.',
            },
          ],
        },
        {
          id: 'data-processing',
          heading: 'Method of Processing Personal Data',
          content: [
            {
              text: 'The Data Controller processes user data in proper order and must take appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of data.\n\nData processing is carried out using computers and/or IT tools, following organizational procedures and modes strictly related to the stated purposes. In addition to the Data Controller, in some cases, data may be accessible to certain individuals responsible and associated with the operation of the application and website (management, sales, marketing, legal services, system administration) or external parties (data processors) appointed if necessary by the Owner.',
            },
            {
              subheading: 'Main Data Processors',
              text: 'To ensure the platform\'s functionality, we use the following main data processors:',
              list: [
                'Google LLC (OAuth authentication) - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>',
                'OpenStreetMap Foundation (geolocation services) - <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">OSM Privacy Policy</a>',
                'LiqPay (payment services) - <a href="https://www.liqpay.ua/documentation/uk/privacy_policy" target="_blank" rel="noopener noreferrer">LiqPay Privacy Policy</a>',
                'Hosting providers (Vercel, Render) - provide data storage and processing',
              ],
            },
            {
              text: 'All data processors are carefully selected and are obligated to ensure an adequate level of personal data protection in accordance with applicable legislation.',
            },
          ],
        },
        {
          id: 'data-location',
          heading: 'Location of Personal Data Processing',
          content: [
            {
              text: 'Data is processed in the data controller\'s operating offices and in any other locations where the parties involved in processing are located. In particular, personal data may be transferred and processed in the countries of the European Economic Area (EEA), Ukraine and beyond their borders.',
            },
            {
              subheading: 'International Data Transfers',
              text: 'Due to the use of third-party services (in particular Google, Telegram and hosting providers), personal data may be transferred outside the European Economic Area and Ukraine. In particular, our hosting providers (Vercel - USA, Render - USA, Germany) are located in the USA and Germany. Such transfers are carried out in compliance with the requirements of data protection legislation.',
              list: [
                'For transfers to the USA and other countries that do not provide an adequate level of protection according to the European Commission\'s decision, we use Standard Contractual Clauses or other appropriate safeguards',
                'We carefully select service providers that ensure an adequate level of data protection',
                'All international data transfers are carried out only to the extent necessary to provide services to users',
              ],
            },
            {
              text: 'The user has the right to obtain additional information about data protection mechanisms in international transfers by contacting us using the contact details specified in this Policy.',
            },
          ],
        },
        {
          id: 'data-retention',
          heading: 'Time of Storage of Personal Data',
          content: [
            {
              text: 'We apply the following specific terms for storing personal data depending on their type and processing purposes:',
            },
            {
              list: [
                'User account data is stored until the account is deleted by the user or for 3 years after the last user activity',
                'Activity logs and technical data (IP addresses, usage data) are stored for 90 days',
                'Data related to purchase history and transactions\n\nWe store information about the fact that you have made a payment for our website services. This information includes:\n\nData necessary to identify the transaction and provide you with access (transaction number in the payment system, date, time, amount, type of purchased subscription);\n\nYour contact details associated with this transaction (email address, account name).\n\nThis data is stored for 5 years in accordance with the requirements of Ukrainian tax legislation (Article 44 of the Tax Code of Ukraine) for the purposes of financial accounting and fulfillment of our legal obligations.\n\nPlease note: Direct processing of payment details (your bank card data) is carried out by our payment system partner LiqPay in accordance with its Privacy Policy and international PCI DSS security standards. <strong>We do not store or have access to your full bank card data.</strong>',
                'Data for marketing purposes is stored for 2 years from the moment of consent or until the user withdraws consent',
                'User photos and content are stored until deleted by the user or for 3 years after the last activity',
                'Chat and support data is stored for 1 year after the conversation is completed',
              ],
            },
            {
              text: 'After the expiration of the specified periods, data is automatically deleted or anonymized. The user can always request early deletion of their data by contacting us using the contact details specified in this Policy.',
            },
          ],
        },
        {
          id: 'data-purposes',
          heading: 'Purpose of Using Collected Personal Data',
          content: [
            {
              text: 'Data relating to the User is collected to allow the Owner to provide its services, as well as for the following purposes:',
            },
            {
              list: [
                'Communication with the User (Legal basis: Legitimate interest or performance of contract)',
                'Interaction with external social networks and platforms (Legal basis: Consent)',
                'Analytics (Legal basis: Consent or legitimate interest)',
                'Registration and authentication (Legal basis: Performance of contract)',
                'Infrastructure monitoring (Legal basis: Legitimate interest)',
                'Traffic optimization and distribution (Legal basis: Legitimate interest)',
                'Interaction with live chat platforms (Legal basis: Legitimate interest)',
                'Remarketing and behavioral targeting (Legal basis: Consent)',
                'Contact management and message sending (Legal basis: Consent or legitimate interest)',
                'Display of content from external platforms (Legal basis: Legitimate interest)',
                'Heat maps (Legal basis: Consent)',
                'Interaction with support and feedback platforms (Legal basis: Legitimate interest)',
                'User database management (Legal basis: Performance of contract)',
                'Support management and contact request handling (Legal basis: Legitimate interest)',
              ],
            },
          ],
        },
        {
          id: 'user-rights',
          heading: 'User Rights',
          content: [
            {
              text: 'According to applicable legislation, the User has the following rights regarding their personal data:',
              list: [
                'Right to access personal data - the ability to obtain information about the processing of your data',
                'Right to rectification of personal data - the ability to correct inaccurate or incomplete data',
                'Right to erasure of personal data ("right to be forgotten") - the ability to delete your data under certain conditions',
                'Right to restriction of processing of personal data - the ability to restrict data processing in certain cases',
                'Right to data portability - the ability to receive your data in a structured format and transfer it to another controller',
                'Right to object to the processing of personal data - the ability to object to data processing based on legitimate interests',
                'Right not to be subject to automated decision-making, including profiling - the ability not to be subject to decisions based solely on automated processing',
              ],
            },
            {
              text: `To exercise these rights, the User can contact us using the Feedback Form available on the website at ${PRIVACY_POLICY_CONFIG.CONTACTS_URL}, or using e-mail: ${PRIVACY_POLICY_CONFIG.EMAIL}. We will review your request within 30 days and provide a response.`,
            },
            {
              text: 'In cases provided by applicable legislation, such as the performance of a legal obligation, protection of legitimate interests in court, ensuring public interest, or for archiving purposes in the public interest, the site may continue to store the User\'s personal data even after receiving a request for deletion.',
            },
            {
              text: 'Please note that we require our registered users to confirm their identity (for example, login/email address, password) before they can access or make changes to their account.',
            },
          ],
        },
        {
          id: 'automated-decisions',
          heading: 'Automated Decision Making',
          content: [
            {
              text: 'We do not use exclusively automated decision-making processes, including profiling, that have legal consequences for users or significantly affect them. All decisions regarding service provision, data processing, or user interaction are made with human involvement.',
            },
            {
              text: 'We may use automated tools for:',
              list: [
                'Analyzing platform usage to improve services',
                'Detecting suspicious activity for security purposes',
                'Personalizing content based on user-provided preferences',
              ],
            },
            {
              text: 'In all cases, the user has the right to human review of any decisions concerning their data and may object to automated processing.',
            },
          ],
        },
        {
          id: 'children-data-protection',
          heading: 'Children\'s Data Protection',
          content: [
            {
              text: `Our services are not intended for persons who have not reached 18 years of age ("adulthood" according to Ukrainian legislation). We do not knowingly collect children's personal data.`,
            },
            {
              text: 'If you are a parent or guardian and discover that your child has provided us with their data without your consent, please contact us. We take measures to delete such information from our servers.',
            },
            {
              text: 'Parents or legal guardians can contact us to:',
              list: [
                'Review their child\'s personal data',
                'Correct or delete such data',
                'Withdraw consent for processing the child\'s data',
              ],
            },
            {
              text: 'We recommend that parents actively monitor their children\'s online activities and use available parental control tools.',
            },
          ],
        },
        {
          id: 'user-responsibility',
          heading: 'User Responsibility for Third-Party Data',
          content: [
            {
              text: 'Users are responsible for any personal data of third parties obtained, published, or posted through this site. Users confirm that they have the consent of third parties to provide such data to the Site Owner. It is prohibited to upload or transmit personal data of third parties without their prior consent.',
            },
          ],
        },
        {
          id: 'policy-changes',
          heading: 'Changes to This Privacy Policy',
          content: [
            {
              text: `The User, by using this website, has accepted the terms of this privacy policy of ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} and recognizes that this privacy policy may change from time to time. Changes made to the privacy policy are published on this page by ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}.`,
            },
            {
              text: 'For significant changes that may affect the processing of personal data (for example, changes in processing purposes or data usage methods), we additionally notify users through active methods, including sending email notifications or notifications through the platform.',
            },
            {
              text: 'Continued use of the site after the changes take effect means your agreement with the updated privacy policy.',
            },
          ],
        },
        {
          id: 'data-controller',
          heading: 'Personal Data Controller',
          content: [
            {
              text: `The personal data controller is ${PRIVACY_POLICY_CONFIG.CONTROLLER_NAME}, ${PRIVACY_POLICY_CONFIG.CONTROLLER_TYPE}, located at: ${PRIVACY_POLICY_CONFIG.CONTROLLER_LOCATION}.`,
            },
            {
              text: `${PRIVACY_POLICY_CONFIG.CONTROLLER_REGISTRATION}`,
            },
            {
              text: `${PRIVACY_POLICY_CONFIG.CONTROLLER_REPRESENTATIVE}`,
            },
            {
              text: `For questions about this privacy policy, the User can contact us through the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}, using e-mail: ${PRIVACY_POLICY_CONFIG.EMAIL} or phone: ${PRIVACY_POLICY_CONFIG.PHONE}.`,
            },
            {
              text: `Contact information is also available on the page: ${PRIVACY_POLICY_CONFIG.CONTACTS_URL}`,
            },
            {
              text: `Contact information is also available in the footer or at the bottom of the page.`,
            },
          ],
        },
      ],
    },
  };

  const currentContent = content[language as 'ua' | 'en'];

  return (
    <div className="privacy-policy-page">
      {/* Main Content */}
      <main className="policy-container">
        <div className="policy-content">
          {/* Title Section */}
          <div className="policy-title-section">
            <h1 className="policy-main-title">{currentContent.title}</h1>
          </div>

          {/* Introduction */}
          <section className="policy-section intro-section">
            <h2 className="policy-section-title">{renderTextWithLinks(currentContent.intro.heading)}</h2>
            <p className="policy-text">{renderTextWithLinks(currentContent.intro.text)}</p>
          </section>

          {/* Content Sections */}
          {currentContent.sections.map((section) => (
            <section key={section.id} id={section.id} className="policy-section">
              <h2 className="policy-section-title">{section.heading}</h2>
              
              {section.content.map((contentItem, contentIndex) => (
                <div key={contentIndex} className="policy-subsection">
                  {'subheading' in contentItem && contentItem.subheading && (
                    <h3 className="policy-subsection-title">{contentItem.subheading}</h3>
                  )}
                  
                  {'text' in contentItem && contentItem.text && (
                    <p className="policy-text">
                      {contentItem.text.split('\n\n').map((paragraph, pIndex, arr) => (
                        <span key={pIndex}>
                          {renderTextWithLinks(paragraph)}
                          {pIndex < arr.length - 1 && (
                            <>
                              <br />
                              <br />
                            </>
                          )}
                        </span>
                      ))}
                    </p>
                  )}
                  
                  {'list' in contentItem && contentItem.list && (
                    <ul className="policy-list">
                      {contentItem.list.map((item: string, itemIndex: number) => (
                        <li key={itemIndex} className="policy-list-item" dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          ))}

          {/* Table of Contents */}
          <nav className="policy-toc">
            <h3 className="toc-title">
              {language === 'ua' ? 'Зміст' : 'Contents'}
            </h3>
            <ul className="toc-list">
              {currentContent.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="toc-link"
                    onClick={handleTocClick(section.id)}
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </main>

      {/* Scroll Button */}
      {showScrollButton && (
        <button 
          className="scroll-button"
          onClick={handleScrollClick}
          title={language === 'ua' ? 'Вверх' : 'Up'}
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
